import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";

const KpiCell = ({ label, value, tone }) => (
  <div className={`kpi${tone ? " kpi--" + tone : ""}`}>
    <div className="kpi__lbl">{label}</div>
    <div className="kpi__num">{value}</div>
  </div>
);

const SlaBanner = ({ data }) => {
  const breached = data?.sla?.breached ?? 0;
  if (breached <= 0) return null;
  return (
    <Alert tone="warn">
      {breached} approval
      {breached === 1 ? " is" : "s are"} past the 48-hour SLA. Prioritize the
      Approvals queue.
    </Alert>
  );
};

function DROverview() {
  // The figures on this screen summarise ONE reviewer's own queue, so they are
  // meaningful to a session that decides (DR, CCO, UDP) and empty for a
  // Platform Manager holding no compliance role. For that oversight session we
  // suppress the credential banner and degrade a 403 or an empty payload to a
  // neutral note instead of a red error. If a later API phase returns
  // platform-wide figures for oversight, the cards render unchanged.
  const { canDecide, isOversight } = useRole();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    drApi
      .overview()
      .then((res) => {
        if (live) setData(res?.data || null);
      })
      .catch((err) => {
        if (live) setError(err?.response?.data?.message || err.message || "Failed to load overview");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Reviewer overview" />

      {canDecide && <CredentialExpiryAlert />}
      {error && !isOversight && <Alert tone="danger">{error}</Alert>}

      {isOversight && !loading && !data && (
        <Alert tone="info">
          These figures summarize an individual reviewer's personal queue and
          are not populated for a Platform Manager session. Operate the live
          queues from the Compliance menu: Investor approvals, Suitability,
          Deals review, and the review audit trail.
        </Alert>
      )}

      {!loading && data && (
        <>
          <SlaBanner data={data} />

          <section className="card">
            <div className="card__head">
              <div className="card__title">Pending reviews</div>
            </div>
            <div className="card__body card__body--flush">
              <div
                className="kpi-rail"
                style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
              >
                <KpiCell label="KYC" value={data.pending?.KYC ?? 0} tone="info" />
                <KpiCell label="Classification" value={data.pending?.CLASSIFICATION ?? 0} tone="info" />
                <KpiCell label="Suitability" value={data.pending?.SUITABILITY ?? 0} tone="info" />
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">SLA health</div>
            </div>
            <div className="card__body card__body--flush">
              <div
                className="kpi-rail"
                style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
              >
                <KpiCell label="Within SLA" value={data.sla?.within ?? 0} tone="ok" />
                <KpiCell label="At risk" value={data.sla?.at_risk ?? 0} tone="warn" />
                <KpiCell label="Breached" value={data.sla?.breached ?? 0} tone="fail" />
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">Credential alerts</div>
            </div>
            <div className="card__body card__body--flush">
              <div
                className="kpi-rail"
                style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
              >
                <KpiCell label="Expiring 30 days" value={data.credentials?.expiring_30d ?? 0} tone="warn" />
                <KpiCell label="Expiring 14 days" value={data.credentials?.expiring_14d ?? 0} tone="warn" />
                <KpiCell label="Expiring 7 days" value={data.credentials?.expiring_7d ?? 0} tone="warn" />
                <KpiCell label="Expired" value={data.credentials?.expired ?? 0} tone="fail" />
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">My decisions</div>
            </div>
            <div className="card__body card__body--flush">
              <div
                className="kpi-rail"
                style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
              >
                <KpiCell label="Last 7 days" value={data.decisions?.last_7_days ?? 0} tone="ok" />
                <KpiCell label="Last 30 days" value={data.decisions?.last_30_days ?? 0} tone="ok" />
              </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

export default DROverview;
